<script lang="ts">
import { onMount } from 'svelte';
import { daysUntil, type UserStats } from '$lib/stats';
import { applyTheme, loadTheme, nextTheme, type Theme } from '$lib/theme';

interface Props {
  stats: UserStats;
  examDate?: string;
}

const { stats, examDate = '2026-12-08' }: Props = $props();

const days = $derived(daysUntil(examDate));
const pct = $derived(
  stats.todayGoal > 0 ? Math.min(100, (stats.todayDone / stats.todayGoal) * 100) : 0,
);

let theme = $state<Theme>('hell');

onMount(() => {
  theme = loadTheme();
  applyTheme(theme);
});

function cycleTheme(): void {
  theme = nextTheme(theme);
  applyTheme(theme);
}
</script>

<header class="topbar">
  <div class="topbar-row">
    <div class="brand">
      <span class="brand-mark" aria-hidden="true">◢◤</span>
      <span class="brand-name">SHK<span class="dot">·</span>WERK</span>
    </div>
    <div class="topbar-right">
      <div class="stat" title="Lern-Streak in Tagen">
        <span class="stat-k">streak</span>
        <span class="stat-v">{stats.streak}<span class="stat-u">d</span>{#if stats.streak > 0}<span class="stat-flame" aria-hidden="true">▲</span>{/if}</span>
      </div>
      <div class="stat" title="Heutige Reviews / Tagesziel">
        <span class="stat-k">heute</span>
        <span class="stat-v">{stats.todayDone}<span class="stat-u">/{stats.todayGoal}</span></span>
        <div class="stat-bar"><div class="stat-bar-fill" style:width="{pct}%"></div></div>
      </div>
      <div class="exam-countdown" title="Gesellenprüfung Teil 2">
        <span class="cd-label">PRÜFUNG IN</span>
        <span class="cd-num">{days}</span>
        <span class="cd-unit">tagen</span>
      </div>
      <button
        type="button"
        class="theme-btn"
        title="Theme wechseln (aktuell: {theme})"
        aria-label="Theme wechseln, aktuell {theme}"
        onclick={cycleTheme}>◐</button>
    </div>
  </div>
</header>

<style>
  .topbar {
    border-bottom: 2px solid var(--rule);
    background: var(--paper-2);
    padding: 8px var(--pad);
    position: sticky;
    top: 0;
    z-index: 20;
  }
  .topbar-row {
    max-width: 880px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .brand { display: flex; align-items: baseline; gap: 8px; }
  .brand-mark { color: var(--accent); font-size: 18px; font-weight: 700; letter-spacing: -2px; }
  .brand-name { font-size: 18px; font-weight: 800; letter-spacing: -0.02em; }
  .dot { color: var(--accent); margin: 0 2px; }

  .topbar-right { display: flex; align-items: center; gap: 14px; }

  .stat { display: flex; flex-direction: column; gap: 1px; min-width: 44px; }
  .stat-k { font-size: 9px; letter-spacing: 0.14em; color: var(--muted); text-transform: uppercase; }
  .stat-v {
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
    display: flex;
    align-items: baseline;
    gap: 2px;
  }
  .stat-u { font-size: 11px; color: var(--muted); font-weight: 500; }
  .stat-flame { font-size: 9px; color: var(--accent); }
  .stat-bar { height: 3px; background: var(--paper-3); margin-top: 2px; }
  .stat-bar-fill { height: 100%; background: var(--accent); }

  .exam-countdown {
    display: flex; align-items: baseline; gap: 6px;
    border: 1px solid var(--rule);
    padding: 4px 10px;
    background: var(--paper);
  }
  .cd-label { font-size: 9px; letter-spacing: 0.12em; color: var(--muted); }
  .cd-num { font-size: 16px; font-weight: 800; color: var(--accent); font-variant-numeric: tabular-nums; }
  .cd-unit { font-size: 9px; letter-spacing: 0.08em; color: var(--muted); text-transform: uppercase; }

  .theme-btn {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--ink-2);
    font-size: 14px;
    line-height: 1;
    padding: 6px 8px;
    cursor: pointer;
  }
  .theme-btn:hover { background: var(--paper-3); }

  @media (max-width: 600px) {
    .cd-label { display: none; }
    .topbar-right { gap: 10px; }
  }
</style>
